import React, { useState, useEffect, ReactElement } from 'react'
import { useFormikContext } from 'formik'

import { LoaderOverContent } from '@mindfulchefuk/components/Loader'
import {
  PaymentFormState,
  TPaymentDetails,
} from '@mindfulchefuk/features/GiftCards/interfaces'
import { CardDetails } from '@mindfulchefuk/design-system/Forms/CardDetails'

import { useGiftCardPaymentPolling } from '@mindfulchefuk/features/GiftCards/hooks/useGiftCardPaymentPolling'

import { CreditCardInput } from '@mindfulchefuk/forms/CardField/CreditCardInput'
import { Check3dsModal } from '@mindfulchefuk/forms/CardField/Check3dsModal'

import { Box, Button, Notification, Text } from '@mindfulchefuk/design-system'

export const PaymentDetails = ({
  redirectUrl,
  giftCardId,
  cardDetails,
  onSuccess,
  onFailure,
  on3dsError,
}: TPaymentDetails): ReactElement => {
  const { isSubmitting, values, errors, setFieldValue, setSubmitting } =
    useFormikContext()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const userHasCard = !!cardDetails
  const hasErrors = !!Object.keys(errors).length

  const disableSubmit =
    (!userHasCard && !(values as PaymentFormState).paymentToken) ||
    hasErrors ||
    isSubmitting

  const { startPolling } = useGiftCardPaymentPolling({
    giftCardId,
    onSuccess: () => {
      setSubmitting(false)
      onSuccess()
    },
    onFailure: ({ reset, error }) => {
      setSubmitting(false)
      setErrorMessage(error.message)
      onFailure(error)
      reset()
    },
  })

  const { paymentToken } = values as PaymentFormState
  useEffect(() => {
    if (paymentToken) {
      setErrorMessage(null)
    }
  }, [paymentToken, setErrorMessage])

  const handle3dsSuccess = () => {
    setSubmitting(true)
    startPolling({ id: giftCardId })
  }

  const handle3dsError = (error: Error) => {
    setSubmitting(false)
    setFieldValue('paymentToken', null)
    setErrorMessage(error.message)
    on3dsError()
  }

  return (
    <Box position="relative">
      {isSubmitting && <LoaderOverContent />}
      <Text as="h3" variant="heading-md" mb={8}>
        Your payment details
      </Text>
      <Box data-testid="payment">
        <Text variant="body-md" mb={16}>
          3D Secure SSL encrypted payment
        </Text>
        {errorMessage && (
          <Notification type="inline" variant="error" my={8}>
            {errorMessage}
          </Notification>
        )}
        <Check3dsModal
          onSuccess={handle3dsSuccess}
          onError={handle3dsError}
          redirectUrl={redirectUrl}
        />
        {cardDetails ? (
          <CardDetails cardDetails={cardDetails} />
        ) : (
          <CreditCardInput />
        )}
      </Box>
      <Box mt={24}>
        <Button
          fullWidth
          type="submit"
          size="medium"
          variant="primary"
          data-testid="cta-place-order"
          disabled={disableSubmit}
        >
          Place order
        </Button>
      </Box>
    </Box>
  )
}
